import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronsRight, HeartHandshake, Users, Newspaper, Sprout, Send, BookOpen, ShieldCheck, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import StoryCard from "../components/StoryCard";
import useReveal from "../hooks/useReveal";
import { FEATURES, STATS, FAQS, IMAGES } from "../mock";
import { fetchStories, sendContact, errorMessage } from "../lib/api";

const ICONS = { HeartHandshake, Users, Newspaper, Sprout };

function Counter({ value, suffix }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const dur = 1600;
        const tick = (t) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(value * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

export default function Home() {
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [latest, setLatest] = useState([]);
  const [hp, setHp] = useState("");
  const startedAt = useRef(Date.now());

  useEffect(() => {
    fetchStories({ limit: 3 }).then((d) => setLatest(d.slice(0, 3))).catch(() => {});
  }, []);

  useReveal([latest.length]);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !/^\S+@\S+\.\S+$/.test(contact.email) || contact.message.length < 10) {
      toast.error("Please fill in your name, a valid email and a message (10+ characters).");
      return;
    }
    setSending(true);
    try {
      await sendContact({ name: contact.name.trim(), email: contact.email.trim(), message: contact.message.trim(), website: hp, started_at: startedAt.current });
      setContact({ name: "", email: "", message: "" });
      toast.success("Message sent! Our team will get back to you shortly.");
    } catch (e2) {
      toast.error(errorMessage(e2));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#4BAF47]/[0.07] blur-3xl pointer-events-none" />
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-5 fade-up">EnviroJustice PR</p>
            <h1 className="text-[#24231D] font-extrabold leading-[1.02] tracking-tight text-[44px] sm:text-[60px] lg:text-[76px] fade-up delay-1">
              Preserving the earth for future generations
            </h1>
            <p className="text-[#878680] text-lg md:text-xl mt-7 max-w-lg leading-relaxed fade-up delay-2">
              A community platform dedicated to showcasing inspiring stories of environmental protection.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up delay-3">
              <Link to="/tell-your-story" className="btn-green">
                Tell Your Story <ChevronsRight size={16} />
              </Link>
              <Link to="/stories" className="btn-outline">
                Discover Stories
              </Link>
            </div>
          </div>
          <div className="relative fade-up delay-2">
            <div className="rounded-xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(36,35,29,0.35)]">
              <img src={IMAGES.heroMain} alt="Community working for the environment" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            <img
              src={IMAGES.heroSmall}
              alt=""
              className="float absolute -bottom-10 -left-6 md:-left-14 w-44 md:w-64 rounded-xl shadow-[0_30px_60px_-20px_rgba(36,35,29,0.4)] border-8 border-white"
            />
            <div className="absolute -top-5 -right-5 bg-[#4BAF47] text-white rounded-xl px-5 py-4 shadow-xl">
              <p className="text-3xl font-extrabold leading-none">400+</p>
              <p className="text-xs font-semibold mt-1 opacity-90">Stories shared</p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="leaf-bg relative">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal grid grid-cols-2 gap-5 order-2 lg:order-1">
            {[
              { icon: BookOpen, title: "Share", text: "Tell your environmental success story through our simple form." },
              { icon: ShieldCheck, title: "Review", text: "Our editorial team checks every story for accuracy and clarity." },
              { icon: Megaphone, title: "Amplify", text: "Approved stories are published and shared with our community." },
              { icon: Sprout, title: "Inspire", text: "Readers take meaningful action for a greener Puerto Rico." },
            ].map((c, i) => (
              <div key={c.title} className={`feature-card bg-white rounded-xl p-7 border border-[#ebe9df] ${i % 2 === 1 ? "translate-y-6" : ""}`}>
                <span className="h-12 w-12 rounded-lg bg-[#4BAF47] text-white flex items-center justify-center"><c.icon size={22} /></span>
                <h3 className="font-extrabold text-xl mt-5">{c.title}</h3>
                <p className="text-sm text-[#878680] mt-2 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
          <div className="reveal order-1 lg:order-2">
            <p className="eyebrow mb-4">Our Mission</p>
            <h2 className="section-title">Stories that spark environmental action</h2>
            <p className="text-[#878680] text-lg mt-6 leading-relaxed">
              EnviroJusticePR is a youth-led 501(c)3 environmental conservation organization. We educate and motivate by featuring real, positive stories of individuals, communities, and organizations making a difference in environmental conservation.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/tell-your-story" className="btn-green">Share Your Story <ChevronsRight size={16} /></Link>
              <Link to="/team" className="btn-outline">Meet The Team</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-[#F8F7F0] relative overflow-hidden">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-24">
          <div className="text-center max-w-3xl mx-auto reveal">
            <p className="eyebrow mb-4">EnviroJusticePR</p>
            <h2 className="section-title">Inspiring global audience to take meaningful actions towards a greener and more sustainable future</h2>
          </div>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={f.id} className="feature-card reveal bg-white rounded-xl p-9 text-center" style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                <img src={f.icon} alt="" className="h-40 w-40 mx-auto object-contain" loading="lazy" />
                <h3 className="text-2xl font-extrabold mt-6 mb-3 hover:text-[#4BAF47] transition-colors">{f.title}</h3>
                <p className="text-[#878680] leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative bg-[#24231D] overflow-hidden">
        <img src={IMAGES.banner} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#24231D]/70 to-[#24231D]/90" />
        <div className="relative max-w-[1320px] mx-auto px-5 lg:px-8 py-24 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s) => {
            const Icon = ICONS[s.icon];
            return (
              <div key={s.id} className="text-center text-white reveal">
                <span className="h-28 w-28 md:h-32 md:w-32 mx-auto rounded-full bg-[#4BAF47] flex items-center justify-center shadow-[0_20px_40px_-15px_rgba(75,175,71,0.8)]">
                  <Icon size={44} strokeWidth={1.5} />
                </span>
                <p className="text-5xl md:text-6xl font-extrabold mt-7">
                  <Counter value={s.value} suffix={s.suffix} />
                </p>
                <p className="font-bold text-lg mt-1">{s.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal relative">
            <img src={IMAGES.about} alt="Reforestation volunteers" className="rounded-xl w-full h-[460px] object-cover shadow-[0_40px_80px_-30px_rgba(36,35,29,0.35)]" />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-[#F8F7F0] rounded-xl p-6 shadow-xl max-w-[260px] border border-[#ebe9df]">
              <p className="font-extrabold text-[#4BAF47] text-3xl">Faster</p>
              <p className="text-sm text-[#878680] mt-1">reception and processing of community reports by competent institutions.</p>
            </div>
          </div>
          <div className="reveal">
            <p className="eyebrow mb-4">About Us</p>
            <h2 className="section-title">Community voices, heard faster</h2>
            <p className="text-[#878680] text-lg mt-6 leading-relaxed">
              Our innovative communication approach facilitates faster reception and processing of community reports and success stories by competent institutions compared to traditional channels. Every published story strengthens the case for environmental justice in Puerto Rico.
            </p>
            <Link to="/stories" className="btn-green mt-10">
              Discover Stories <ChevronsRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ + CONTACT */}
      <section id="contact" className="leaf-bg scroll-mt-24">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-3 reveal">
            <p className="eyebrow mb-4">FAQ</p>
            <h2 className="section-title">Frequently Asked Questions - EnviroJustice PR</h2>
            <p className="text-[#878680] mt-5 mb-10">Have a question not answered here? Feel free to reach out to our support team by submitting the contact form.</p>
            <Accordion type="single" collapsible defaultValue="faq-1" className="space-y-4">
              {FAQS.map((f) => (
                <AccordionItem key={f.id} value={`faq-${f.id}`} className="bg-white rounded-xl border border-[#ebe9df] px-6 data-[state=open]:shadow-[0_20px_40px_-25px_rgba(36,35,29,0.3)]">
                  <AccordionTrigger className="text-left text-lg font-extrabold hover:no-underline hover:text-[#4BAF47] py-5 [&[data-state=open]]:text-[#4BAF47]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#878680] text-base leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="lg:col-span-2 reveal">
            <div className="bg-[#24231D] text-white rounded-xl p-8 md:p-10 sticky top-32">
              <p className="eyebrow mb-3">Talk to us</p>
              <h3 className="text-3xl font-extrabold leading-tight">Do you have any question?</h3>
              <form onSubmit={submitContact} className="mt-8 space-y-4">
                <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
                </div>
                <input
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  placeholder="Your Name"
                  data-testid="contact-name"
                  className="w-full h-13 py-3.5 px-4 rounded-md bg-white/10 border border-white/15 placeholder:text-white/50 outline-none focus:ring-2 focus:ring-[#4BAF47] transition-shadow"
                />
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder="Email Address"
                  data-testid="contact-email"
                  className="w-full py-3.5 px-4 rounded-md bg-white/10 border border-white/15 placeholder:text-white/50 outline-none focus:ring-2 focus:ring-[#4BAF47] transition-shadow"
                />
                <textarea
                  rows={5}
                  value={contact.message}
                  onChange={(e) => setContact({ ...contact, message: e.target.value })}
                  placeholder="Your Message"
                  data-testid="contact-message"
                  className="w-full py-3.5 px-4 rounded-md bg-white/10 border border-white/15 placeholder:text-white/50 outline-none focus:ring-2 focus:ring-[#4BAF47] transition-shadow resize-none"
                />
                <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-green w-full justify-center disabled:opacity-70">
                  {sending ? "Sending..." : "Send Message"} <Send size={16} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* DISCOVER STORIES */}
      <section className="relative">
        <div className="relative h-[380px] overflow-hidden">
          <img src={IMAGES.banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#24231D]/60" />
          <div className="relative max-w-[1320px] mx-auto px-5 lg:px-8 h-full flex flex-col justify-center text-white reveal">
            <p className="eyebrow mb-3">Start Reading</p>
            <h2 className="text-5xl md:text-6xl font-extrabold">Discover Stories</h2>
            <Link to="/stories" className="btn-green mt-8 self-start">
              Start Reading <ChevronsRight size={16} />
            </Link>
          </div>
        </div>
        <div className="bg-white">
          <div className="max-w-[1320px] mx-auto px-5 lg:px-8 -mt-20 pb-24 grid md:grid-cols-3 gap-6 relative">
            {latest.map((s, i) => (
              <div key={s.id} className="reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <StoryCard story={s} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
